import type { AtRiskRow, MitigationRung, MitigationTimeline } from '../api/mitigationService'

export const MITIGATION_RUNGS: MitigationRung[] = [
  'WIDEN_DISPATCH',
  'REPRICE',
  'SHIFT_PICKUP_WINDOW',
  'PREFER_STRONGER_SHIPPER',
]

export type OpsSuggestedAction =
  | 'MITIGATE_NOW'
  | 'APPLY_REPRICE'
  | 'EXTEND_AND_MITIGATE'
  | 'SOURCE_CARRIER'
  | 'REVIEW_TIMELINE'
  | 'MANUAL_PLACEMENT'

export interface OpsGuidance {
  action: OpsSuggestedAction
  label: string
  detail: string
  severity: 'info' | 'warning' | 'error'
}

export interface RepriceState {
  priceEscalationStep?: number | null
  priceEscalationApprovedStep?: number | null
  priceEscalationProposedAt?: string | null
  priceEscalationDecidedAt?: string | null
  awaitingShipperDecision?: boolean | null
}

export function hasOutstandingReprice(row: RepriceState): boolean {
  if (row.awaitingShipperDecision === true) return true
  if (row.awaitingShipperDecision === false) return false
  if (row.priceEscalationProposedAt && !row.priceEscalationDecidedAt) return true
  return (
    row.priceEscalationStep != null &&
    row.priceEscalationApprovedStep == null &&
    !row.priceEscalationDecidedAt
  )
}

export function allRungsAttempted(attempted: MitigationRung[] = []): boolean {
  return MITIGATION_RUNGS.every((rung) => attempted.includes(rung))
}

/** Flow 2 decision tree — what Ops should do next for an at-risk row. */
export function getAtRiskGuidance(row: AtRiskRow): OpsGuidance {
  const attempted = row.rungsAttempted || []
  const hoursLeft = row.hoursToDeadline ?? Infinity

  if (hasOutstandingReprice(row)) {
    return {
      action: 'APPLY_REPRICE',
      label: 'Phone shipper — reprice',
      detail:
        'REPRICE is outstanding with no answer. Call the shipper; if they agree, apply the approved raise.',
      severity: 'warning',
    }
  }

  if (attempted.length === 0 && hoursLeft > 0) {
    return {
      action: 'MITIGATE_NOW',
      label: 'Mitigate now',
      detail: 'No rungs attempted yet — the ladder may not have reached this request. Run mitigate-now.',
      severity: 'info',
    }
  }

  if (allRungsAttempted(attempted)) {
    if (hoursLeft > 2) {
      return {
        action: 'EXTEND_AND_MITIGATE',
        label: 'Extend + mitigate',
        detail:
          'All rungs attempted with time left. Phone the shipper about moving pickup; if yes, extend deadline and mitigate-now.',
        severity: 'warning',
      }
    }
    return {
      action: 'SOURCE_CARRIER',
      label: 'Source carrier now',
      detail:
        'All rungs attempted and deadline is near. Start sourcing a carrier — do not wait for the give-up queue.',
      severity: 'error',
    }
  }

  return {
    action: 'REVIEW_TIMELINE',
    label: 'Review timeline',
    detail: 'Open the timeline, read rung history, and pick the next ops action.',
    severity: 'info',
  }
}

/** Flow 3 — guidance when viewing a given-up request. */
export function getGivenUpGuidance(): OpsGuidance {
  return {
    action: 'MANUAL_PLACEMENT',
    label: 'Recover or close',
    detail:
      'Read the timeline first. Phone the shipper before sourcing. Place a carrier manually, return to matching, or let the shipper cancel.',
    severity: 'warning',
  }
}

/** Flow 4 — guidance when a raise is outstanding on any request. */
export function getTimelineGuidance(timeline: MitigationTimeline): OpsGuidance | null {
  if (timeline.givenUpAt) {
    return getGivenUpGuidance()
  }

  if (hasOutstandingReprice(timeline)) {
    return {
      action: 'APPLY_REPRICE',
      label: 'Outstanding reprice',
      detail:
        'A price raise is waiting on the shipper. Answer only from what is on screen — if they agreed by phone, apply the raise.',
      severity: 'warning',
    }
  }

  const attempted = timeline.attemptedMitigationRungs || []
  if (allRungsAttempted(attempted) && (timeline.hoursToDeadline ?? 0) < 6) {
    return {
      action: 'SOURCE_CARRIER',
      label: 'Deadline pressure',
      detail: 'All ladder rungs spent and time is short. Consider manual placement if automated paths are exhausted.',
      severity: 'error',
    }
  }

  return null
}

export const OPS_ACTION_LABELS: Record<OpsSuggestedAction, string> = {
  MITIGATE_NOW: 'Mitigate now',
  APPLY_REPRICE: 'Apply raise',
  EXTEND_AND_MITIGATE: 'Extend + mitigate',
  SOURCE_CARRIER: 'Source carrier',
  REVIEW_TIMELINE: 'Review',
  MANUAL_PLACEMENT: 'Manual placement',
}
